import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clapperboard, Film, Workflow } from 'lucide-react';

/**
 * Resolve Scene / Editor / Workflow paths for narration or story projects.
 */
export function editorViewPaths({ mode = 'story', projectId, storyId } = {}) {
  if (mode === 'reels_shorts' && projectId) {
    const flow = `/reels-shorts/create?project=${projectId}`;
    return {
      scene: flow,
      editor: `/reels-shorts/${projectId}/video-editor`,
      workflow: flow,
    };
  }
  if (mode === 'narration' && projectId) {
    return {
      scene: `/narration/${projectId}/edit`,
      editor: `/narration/${projectId}/video-editor`,
      workflow: `/narration/${projectId}/workflow`,
    };
  }
  const id = storyId || projectId;
  return {
    scene: `/stories/${id}/edit`,
    editor: `/stories/${id}/video-editor`,
    workflow: `/stories/${id}/workflow`,
  };
}

const TABS = [
  { id: 'scene', label: 'Scene View', icon: Film },
  { id: 'editor', label: 'Editor View', icon: Clapperboard },
  { id: 'workflow', label: 'Workflow', icon: Workflow },
];

/**
 * Top-of-screen tabs switching Scene grid ↔ Video editor ↔ Node workflow canvas.
 * All three routes show the same story/project — different representations only.
 */
export function EditorViewTabs({
  active = 'scene',
  mode = 'story',
  projectId,
  storyId,
  onBeforeNavigate,
  className = '',
}) {
  const navigate = useNavigate();
  const paths = editorViewPaths({ mode, projectId, storyId });

  const go = async (tabId) => {
    if (tabId === active) return;
    const path = paths[tabId];
    if (!path) return;
    if (typeof onBeforeNavigate === 'function') {
      const ok = await onBeforeNavigate(tabId, path);
      if (ok === false) return;
    }
    navigate(path);
  };

  return (
    <div
      className={`inline-flex items-center gap-0.5 p-0.5 rounded-xl border border-white/10 bg-black/40 backdrop-blur-md ${className}`}
      role="tablist"
      aria-label="Editor views"
      data-testid="editor-view-tabs"
    >
      {TABS.map((tab) => {
        const Icon = tab.icon;
        const isActive = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => go(tab.id)}
            data-testid={`editor-view-tab-${tab.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              isActive
                ? 'bg-white/15 text-white border border-white/20 shadow-sm'
                : 'text-[#9CA3AF] hover:text-white border border-transparent hover:bg-white/5'
            }`}
          >
            <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-[#62FAE3]' : ''}`} />
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.id === 'workflow' ? 'Nodes' : tab.id === 'editor' ? 'Editor' : 'Scenes'}</span>
          </button>
        );
      })}
    </div>
  );
}

export default EditorViewTabs;
